// src/pages/JSONValidator/JSONValidator.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Code, CheckCircle, AlertCircle, Search,
    Terminal, Copy, Settings, ChevronRight, Wand2, FileText,
    Shield, Zap, Upload, Info, ClipboardPaste, FileJson
} from 'lucide-react';
import { jsonrepair } from 'jsonrepair';
import { JSONPath } from 'jsonpath-plus';
import Ajv, { ErrorObject } from 'ajv';
import SEO from '../../../components/SEO/SEO';
import { seoConfig } from '../../../config/seo';
import { ToolInfoStrip, ToolSection, ToolStepCard, ToolFeatureCard, ToolFaqCta, ToolRelatedCard, ToolPageLayout } from '../components';

const ajv = new Ajv({ allErrors: true, strict: false });

export interface JSONStats {
    size: number;
    lines: number;
    objects: number;
    isArray: boolean;
}

export interface ErrorInfo {
    line: number;
    originalLine: number;
    col: number | null;
    isHeuristic: boolean;
}

const JSONValidator: React.FC = () => {
    const [jsonInput, setJsonInput] = useState<string>('');
    const [jsonPath, setJsonPath] = useState<string>('');
    const [schemaInput, setSchemaInput] = useState<string>('');
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [repairable, setRepairable] = useState<boolean>(false);
    const [pathResult, setPathResult] = useState<any>(null);
    const [schemaErrors, setSchemaErrors] = useState<ErrorObject[] | any[]>([]);
    const [stats, setStats] = useState<JSONStats>({ size: 0, lines: 0, objects: 0, isArray: false });
    const [parsedData, setParsedData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [dragActive, setDragActive] = useState<boolean>(false);
    const [lineCount, setLineCount] = useState<number>(1);
    const [isWrapped, setIsWrapped] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [highlightedLine, setHighlightedLine] = useState<number | null>(null);

    const editorRef = useRef<HTMLTextAreaElement>(null);
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Validate JSON
    const validateJSON = useCallback((input: string) => {
        if (!input.trim()) {
            setIsValid(null);
            setError(null);
            setRepairable(false);
            setPathResult(null);
            setParsedData(null);
            setSchemaErrors([]);
            return;
        }

        try {
            const parsed = JSON.parse(input);
            setIsValid(true);
            setError(null);
            setRepairable(false);

            // Calculate stats
            const size = new Blob([input]).size;
            const lines = input.split('\n').length;
            const isArray = Array.isArray(parsed);
            const objects = isArray ? parsed.length : Object.keys(parsed).length;

            setParsedData(parsed);
            setStats({ size, lines, objects, isArray });

            // Run JSONPath if present
            if (jsonPath.trim()) {
                try {
                    const result = JSONPath({ path: jsonPath, json: parsed });
                    setPathResult(result);
                } catch (e) {
                    setPathResult(null);
                }
            } else {
                setPathResult(null);
            }

            // Run Schema Validation if present
            if (schemaInput.trim()) {
                try {
                    const schema = JSON.parse(schemaInput);
                    const validate = ajv.compile(schema);
                    const validSchema = validate(parsed);
                    setSchemaErrors(validSchema ? [] : (validate.errors || []));
                } catch (e) {
                    setSchemaErrors([{ message: 'Invalid Schema JSON' }]);
                }
            } else {
                setSchemaErrors([]);
            }

        } catch (e: any) {
            setIsValid(false);
            setError(e.message);
            setPathResult(null);
            setParsedData(null);

            // Check if repairable
            try {
                jsonrepair(input);
                setRepairable(true);
            } catch (err) {
                setRepairable(false);
            }
        }
    }, [jsonPath, schemaInput]);

    useEffect(() => {
        validateJSON(jsonInput);
        setLineCount(jsonInput.split('\n').length || 1);
    }, [jsonInput, validateJSON]);

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (lineNumbersRef.current) {
            lineNumbersRef.current.scrollTop = (e.target as HTMLTextAreaElement).scrollTop;
        }
    };

    const scrollToLine = (line: number, col: number = 1, shouldFocus: boolean = true) => {
        if (editorRef.current && line > 0) {
            // Use requestAnimationFrame to ensure scroll happens after any pending state/render
            requestAnimationFrame(() => {
                const lineHeight = 21; // Matches CSS
                const targetScroll = ((line - 1) * lineHeight) - 60;

                // Move cursor to the target line to avoid focus conflicts
                const lines = jsonInput.split('\n');
                const charIndexBeforeLine = lines.slice(0, line - 1).reduce((acc, l) => acc + l.length + 1, 0);
                const charIndex = charIndexBeforeLine + (Math.max(1, col) - 1);

                if (editorRef.current) {
                    editorRef.current.scrollTop = targetScroll;
                    editorRef.current.setSelectionRange(charIndex, charIndex);

                    // Set highlight
                    setHighlightedLine(line);
                    if (shouldFocus) {
                        editorRef.current.focus();
                    }

                    // Double check sync after a short delay
                    setTimeout(() => {
                        if (lineNumbersRef.current && editorRef.current) {
                            lineNumbersRef.current.scrollTop = editorRef.current.scrollTop;
                        }
                    }, 50);

                    // Clear highlight after 3 seconds
                    setTimeout(() => setHighlightedLine(null), 3000);
                }
            });
        }
    };

    const parseErrorInfo = (errMsg: string | null): ErrorInfo | null => {
        if (!errMsg) return null;

        let line: number | null = null;
        let col: number | null = null;
        let pos: number | null = null;

        const lineMatch = errMsg.match(/line (\d+)/i);
        const colMatch = errMsg.match(/column (\d+)/i);
        const posMatch = errMsg.match(/position (\d+)/i);

        if (lineMatch) line = parseInt(lineMatch[1]);
        if (colMatch) col = parseInt(colMatch[1]);

        if (posMatch && line === null) {
            pos = parseInt(posMatch[1]);
            const textBefore = jsonInput.substring(0, pos);
            const lines = textBefore.split('\n');
            line = lines.length;
            col = lines[lines.length - 1].length + 1;
        }

        if (line !== null) {
            let isHeuristic = false;
            let targetLine = line;

            // Heuristic for "Missing Comma" error offset:
            if (line > 1 && (errMsg.includes('Unexpected string') || errMsg.includes('Unexpected token') || errMsg.includes('Expected \',\''))) {
                const lines = jsonInput.split('\n');
                const prevLine = lines[line - 2]?.trim();
                if (prevLine && !prevLine.endsWith(',') && !prevLine.endsWith('{') && !prevLine.endsWith('[')) {
                    targetLine = line - 1;
                    isHeuristic = true;
                    // For missing comma, point to the very end of the previous line
                    col = lines[line - 2].length + 1;
                }
            }
            return { line: targetLine, originalLine: line, col, isHeuristic };
        }

        return null;
    };

    const findLineOfPath = (path: string | undefined): number | null => {
        if (!path || !jsonInput) return null;
        const segments = path.split('/').filter(Boolean);
        if (segments.length === 0) return 1;

        const lastSegment = segments[segments.length - 1];
        const lines = jsonInput.split('\n');

        // Search for the last segment (key) in the JSON
        // This is a heuristic search
        for (let i = 0; i < lines.length; i++) {
            if (lines[i].includes(`"${lastSegment}"`)) {
                return i + 1;
            }
        }
        return null;
    };

    const handleBeautify = () => {
        try {
            if (isValid) {
                setJsonInput(JSON.stringify(JSON.parse(jsonInput), null, 4).trim());
            } else if (repairable) {
                setJsonInput(JSON.stringify(JSON.parse(jsonrepair(jsonInput)), null, 4).trim());
            }
        } catch (e) { }
    };

    const handleMinify = () => {
        try {
            if (isValid) {
                setJsonInput(JSON.stringify(JSON.parse(jsonInput)).trim());
            } else if (repairable) {
                setJsonInput(JSON.stringify(JSON.parse(jsonrepair(jsonInput))).trim());
            }
        } catch (e) { }
    };

    const handleRepair = () => {
        try {
            const repaired = jsonrepair(jsonInput);
            setJsonInput(JSON.stringify(JSON.parse(repaired), null, 4).trim());
        } catch (e) {
            console.error('Auto-repair failed:', e);
            alert('Could not repair this JSON automatically. Please check the error below.');
        }
    };

    const handleCopy = (text: string) => {
        if (text) {
            navigator.clipboard.writeText(text);
        }
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Handle File Upload
    const handleFileUpload = async (file: File) => {
        setLoading(true);
        setError(null);
        try {
            const text = await file.text();
            setJsonInput(text);
            setFileName(file.name);
        } catch (err: any) {
            setError('Failed to read file: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setJsonInput('');
        setFileName('');
        setError(null);
        setIsValid(null);
        setSchemaErrors([]);
        setPathResult(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        if (!query || !editorRef.current) return;

        const content = jsonInput;
        const index = content.toLowerCase().indexOf(query.toLowerCase());

        if (index !== -1) {
            editorRef.current.setSelectionRange(index, index + query.length);

            // Scroll to line without stealing focus
            const linesBefore = content.substring(0, index).split('\n').length;
            scrollToLine(linesBefore, 1, false);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileUpload(e.dataTransfer.files[0]);
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        const clipboardData = e.clipboardData;
        if (clipboardData.files && clipboardData.files.length > 0) {
            e.preventDefault();
            handleFileUpload(clipboardData.files[0]);
        }
    };

    return (
        <>
            <SEO
                {...seoConfig.jsonValidator}
                schema={[
                    {
                        "@context": "https://schema.org",
                        "@type": "WebApplication",
                        "name": "Professional JSON Validator & Power Utility",
                        "description": "Free online JSON tool. Validate, format, query, and repair JSON data instantly. Features smart auto-fix and JSON Path support.",
                        "url": "https://www.filefusion.app/tools/json-validator-formatter",
                        "applicationCategory": "DeveloperApplication",
                        "operatingSystem": "Web Browser",
                        "featureList": [
                            "Real-time JSON validation",
                            "Smart Auto-Fix for syntax errors",
                            "JSON Path query support",
                            "JSON Schema validation",
                            "Beautify and Minify JSON",
                            "100% Client-side processing"
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": [
                            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.filefusion.app/" },
                            { "@type": "ListItem", "position": 2, "name": "Tools", "item": "https://www.filefusion.app/tools" },
                            { "@type": "ListItem", "position": 3, "name": "JSON Validator", "item": "https://www.filefusion.app/tools/json-validator-formatter" }
                        ]
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "HowTo",
                        "name": "How to Validate and Repair JSON Online",
                        "description": "Follow these simple steps to validate, format, and fix your JSON data.",
                        "totalTime": "PT1M",
                        "step": [
                            { "@type": "HowToStep", "position": 1, "name": "Paste Your JSON", "text": "Paste your raw JSON content into the editor." },
                            { "@type": "HowToStep", "position": 2, "name": "Fix Errors", "text": "Use Smart Auto-Repair if any syntax errors are detected." },
                            { "@type": "HowToStep", "position": 3, "name": "Format or Query", "text": "Beautify for readability or use JSON Path to extract specific data." }
                        ]
                    }
                ]}
            />

            <ToolPageLayout
                title="Free JSON Validator & Power Tool"
                subtitle="Validate, format, and repair JSON instantly. Use JSON Path to query data and validate against custom Schemas—all processed 100% locally in your browser."
                icon={<FileJson size={32} strokeWidth={1.5} className="text-[#1a73e8]" />}
            >
                <div className="flex flex-col gap-[32px] mb-[64px]">
                    <div className="tool-main-content">
                        <div className="flex flex-col gap-[24px] mb-[40px] w-full">
                            {/* JSON Input Section */}
                            <div
                                className={`bg-white border-[1px] ${isValid === true ? 'border-[2px] border-[#1e8e3e] shadow-[0_0_0_4px_rgba(30,142,62,0.05)]' : isValid === false ? 'border-[2px] border-[#d93025] shadow-[0_0_0_4px_rgba(217,48,37,0.05)]' : 'border-[#dadce0]'} ${dragActive ? 'border-[2px] border-dashed border-[#1a73e8] bg-[#e8f0fe]' : ''} rounded-[12px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] overflow-hidden flex flex-col w-full flex-1 transition-all duration-300`}
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                            >
                                <div className="px-[16px] py-[10px] bg-[#f8f9fa] border-b-[1px] border-[#dadce0] flex justify-between items-center">
                                    <div className="flex items-center gap-[10px]">
                                        <h3 className="text-[0.875rem] font-semibold text-[#202124] m-0 flex items-center gap-[8px]">
                                            <Terminal size={16} /> {fileName ? `File: ${fileName}` : 'JSON Input'}
                                        </h3>
                                        <div className="flex items-center gap-[8px]">
                                            <div className={`flex items-center gap-[5px] px-[8px] py-[2px] rounded-[6px] text-[0.75rem] font-semibold border-[1px] border-transparent ${isValid === true ? 'bg-[#e6fffa] text-[#1e8e3e]' : isValid === false ? 'bg-[#fdf2f2] text-[#d93025]' : 'bg-[#f1f3f4] text-[#5f6368]'}`}>
                                                {isValid === true ? <CheckCircle size={12} /> :
                                                    isValid === false ? <AlertCircle size={12} /> :
                                                        <Terminal size={12} />}
                                                <span>{isValid === true ? 'Valid' : isValid === false ? 'Invalid' : 'Waiting'}</span>
                                            </div>
                                            {isValid && (
                                                <div className="flex gap-[6px]">
                                                    <span className="text-[0.75rem] font-medium text-[#5f6368] bg-white border-[1px] border-[#dadce0] px-[8px] py-[2px] rounded-[6px] flex items-center">{formatSize(stats.size)}</span>
                                                    <span className="text-[0.75rem] font-medium text-[#5f6368] bg-white border-[1px] border-[#dadce0] px-[8px] py-[2px] rounded-[6px] flex items-center">{stats.lines} Lines</span>
                                                    <span className="text-[0.75rem] font-medium text-[#5f6368] bg-white border-[1px] border-[#dadce0] px-[8px] py-[2px] rounded-[6px] flex items-center">{stats.objects} {stats.isArray ? 'Items' : 'Keys'}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-[8px]">
                                        <div className="flex items-center gap-[8px] px-[10px] py-[4px] bg-white border-[1px] border-[#dadce0] rounded-[8px] mr-[4px] transition-all duration-200 focus-within:border-[#1a73e8] focus-within:shadow-[0_0_0_3px_#e8f0fe]">
                                            <Search size={14} />
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchQuery}
                                                onChange={(e) => handleSearch(e.target.value)}
                                                className="border-none outline-none text-[0.8125rem] w-[160px] bg-transparent text-[#202124]"
                                            />
                                        </div>
                                        <button
                                            className={`px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] flex items-center gap-[6px] cursor-pointer transition-all duration-200 ${isWrapped ? 'bg-[#e8f0fe] text-[#1a73e8] border-[#1a73e8]' : 'bg-white text-[#202124] border-[#dadce0] hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]'}`}
                                            onClick={() => setIsWrapped(!isWrapped)}
                                            title="Toggle Word Wrap"
                                        >
                                            <FileText size={14} /> Wrap
                                        </button>
                                        <button className="px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#dadce0] bg-white text-[#202124] flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]" onClick={() => handleCopy(jsonInput)}>
                                            <Copy size={14} /> Copy
                                        </button>
                                        <button className="px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#dadce0] bg-white text-[#202124] flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]" onClick={() => fileInputRef.current?.click()}>
                                            <Upload size={14} /> Load File
                                        </button>
                                        <button className="px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#dadce0] bg-white text-[#202124] flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]" onClick={handleBeautify}><Settings size={14} /> Beautify</button>
                                        <button className="px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#dadce0] bg-white text-[#202124] flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]" onClick={handleMinify}><Terminal size={14} /> Minify</button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden-file-input"
                                            onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(e.target.files[0])}
                                            accept=".json,.txt"
                                            style={{ display: 'none' }}
                                        />
                                    </div>
                                </div>

                                {repairable && (
                                    <div className="bg-[#fff8e1] border-b-[1px] border-[#ffe082] px-[20px] py-[12px] flex justify-between items-center text-[0.875rem]">
                                        <div className="flex items-center gap-[10px] color-[#b45309]">
                                            <Wand2 size={16} />
                                            <span>Syntax errors detected. We can fix them automatically.</span>
                                        </div>
                                        <button className="px-[16px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#1a73e8] bg-[#1a73e8] text-white flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#1557b0] hover:shadow-[0_1px_3px_1px_rgba(66,133,244,0.15)]" onClick={handleRepair}>Auto-Repair</button>
                                    </div>
                                )}

                                <div className="relative h-[480px] flex w-full bg-white">
                                    {!isWrapped && (
                                        <div className="w-[50px] bg-[#f8f9fa] border-r-[1px] border-[#dadce0] py-[20px] px-0 text-center select-none overflow-hidden text-[#5f6368] font-mono text-[0.8125rem] leading-[1.5]" ref={lineNumbersRef}>
                                            {Array.from({ length: lineCount }).map((_, i) => (
                                                <div
                                                    key={i}
                                                    className={`h-[21px] flex items-center justify-center transition-all duration-300 ${highlightedLine === i + 1 ? 'bg-[#d93025] text-white font-bold rounded-[2px]' : ''}`}
                                                >
                                                    {i + 1}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <textarea
                                        ref={editorRef}
                                        className={`flex-1 w-full h-full border-none p-[20px] font-mono text-[0.875rem] leading-[1.5] resize-none text-[#202124] outline-none bg-transparent z-10 placeholder-inherit ${isWrapped ? 'whitespace-pre-wrap break-all' : ''}`}
                                        placeholder=""
                                        value={jsonInput}
                                        onChange={(e) => setJsonInput(e.target.value)}
                                        onPaste={handlePaste}
                                        onScroll={handleScroll}
                                        spellCheck="false"
                                        wrap={isWrapped ? "soft" : "off"}
                                    />
                                    {!jsonInput && !dragActive && (
                                        <div className="absolute top-0 left-[50px] right-0 bottom-0 flex items-center justify-center pointer-events-none z-0">
                                            <div className="flex flex-col items-center gap-[16px] text-[#5f6368] text-center">
                                                <div className="flex items-center gap-[20px] opacity-40">
                                                    <Upload size={32} />
                                                    <ClipboardPaste size={32} />
                                                    <Code size={32} />
                                                </div>
                                                <div className="max-w-[300px]">
                                                    <h4 className="text-[1rem] font-semibold m-0 mb-[8px] text-[#5f6368]">Quick Start Guide</h4>
                                                    <p className="text-[0.8125rem] m-0 leading-[1.5]">Type directly, paste JSON from clipboard, or drop a file anywhere to begin.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {dragActive && (
                                        <div className="absolute top-0 left-0 right-0 bottom-0 bg-[rgba(26,115,232,0.08)] flex flex-col items-center justify-center gap-[16px] text-[#1a73e8] z-20 pointer-events-none backdrop-blur-[2px]">
                                            <Upload size={32} />
                                            <span className="text-[1.125rem] font-semibold">Drop JSON File to Load</span>
                                        </div>
                                    )}
                                </div>

                                <div className="px-[20px] py-[10px] border-t-[1px] border-[#dadce0] flex items-center min-h-[48px] bg-[#f8f9fa]">
                                    {error ? (
                                        <div className="flex items-center gap-[10px] text-[0.8125rem] font-medium text-[#d93025]">
                                            <AlertCircle size={14} />
                                            <span className="max-w-[600px] whitespace-nowrap overflow-hidden text-ellipsis">{error.replace(/\s*at position \d+/i, '').replace(/\s*\(line \d+ column \d+\)\s*$/i, '').trim()}</span>
                                            {parseErrorInfo(error) && (
                                                <div className="flex items-center gap-[12px]">
                                                    <button
                                                        className="bg-white text-[#d93025] border-[1px] border-[#d93025] px-[12px] py-[4px] rounded-[6px] text-[0.75rem] font-semibold cursor-pointer transition-all duration-200 hover:bg-[#fef2f2] hover:shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                                                        onClick={() => {
                                                            const info = parseErrorInfo(error);
                                                            if (info) scrollToLine(info.line, info.col || 1);
                                                        }}
                                                    >
                                                        Go to line {parseErrorInfo(error)?.line}
                                                    </button>
                                                    {parseErrorInfo(error)?.col && (
                                                        <span className="text-[0.75rem] font-semibold text-[#d93025] opacity-80">Col {parseErrorInfo(error)?.col}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : isValid === true ? (
                                        <div className="flex items-center gap-[10px] text-[0.8125rem] font-medium text-[#1e8e3e]">
                                            <CheckCircle size={14} />
                                            <span>JSON is perfectly valid and well-formatted.</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-[10px] text-[0.8125rem] font-medium text-[#5f6368]">
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Power Features Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-[24px] mb-[24px]">
                                <div className="bg-white border-[1px] border-[#dadce0] rounded-[12px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                                    <div className="px-[16px] py-[10px] bg-[#f8f9fa] border-b-[1px] border-[#dadce0] flex justify-between items-center">
                                        <h3 className="text-[0.875rem] font-semibold text-[#202124] m-0 flex items-center gap-[8px]">
                                            <Search size={18} /> JSON Path Query
                                            <div className="inline-flex items-center justify-center ml-[8px] text-[#5f6368] cursor-help transition-colors duration-200 hover:text-[#1a73e8]" title="Enter a JSONPath expression (e.g., $.users[*].name) to filter and extract specific data from your JSON.">
                                                <Info size={14} />
                                            </div>
                                        </h3>
                                    </div>
                                    <div className="p-[24px]">
                                        <div className="flex flex-col gap-[12px]">
                                            <input
                                                type="text"
                                                className="w-full px-[14px] py-[12px] border-[1px] border-[#dadce0] rounded-[8px] text-[0.9375rem] font-inherit outline-none transition-all duration-200 focus:border-[#1a73e8] focus:shadow-[0_0_0_3px_#e8f0fe]"
                                                placeholder="e.g. $.store.book[*].author"
                                                value={jsonPath}
                                                onChange={(e) => setJsonPath(e.target.value)}
                                            />
                                        </div>
                                        <p className="text-[#5f6368] text-[0.9375rem] leading-[1.6]">
                                            Use standard JSONPath syntax to filter and extract data.
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white border-[1px] border-[#dadce0] rounded-[12px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                                    <div className="px-[16px] py-[10px] bg-[#f8f9fa] border-b-[1px] border-[#dadce0] flex justify-between items-center">
                                        <h3 className="text-[0.875rem] font-semibold text-[#202124] m-0 flex items-center gap-[8px]">
                                            <FileText size={18} /> Schema Validation
                                            <div className="inline-flex items-center justify-center ml-[8px] text-[#5f6368] cursor-help transition-colors duration-200 hover:text-[#1a73e8]" title="Paste a JSON Schema object here to validate your data against specific business rules and types.">
                                                <Info size={14} />
                                            </div>
                                        </h3>
                                    </div>
                                    <div className="p-[24px]">
                                        <div className="flex flex-col gap-[12px]">
                                            <textarea
                                                className="w-full px-[14px] py-[12px] border-[1px] border-[#dadce0] rounded-[8px] text-[0.9375rem] font-inherit outline-none transition-all duration-200 focus:border-[#1a73e8] focus:shadow-[0_0_0_3px_#e8f0fe]"
                                                style={{ height: '42px', resize: 'none' }}
                                                placeholder="Paste JSON Schema here..."
                                                value={schemaInput}
                                                onChange={(e) => setSchemaInput(e.target.value)}
                                            />
                                        </div>
                                        <p className="text-[#5f6368] text-[0.9375rem] leading-[1.6]">
                                            Validate business logic against a JSON Schema object.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Results Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-[24px] mb-[24px]">
                                <div className="bg-white border-[1px] border-[#dadce0] rounded-[12px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                                    <div className="px-[16px] py-[10px] bg-[#f8f9fa] border-b-[1px] border-[#dadce0] flex justify-between items-center">
                                        <h3 className="text-[0.875rem] font-semibold text-[#202124] m-0 flex items-center gap-[8px]">
                                            <ChevronRight size={16} /> Query Results
                                            <div className="inline-flex items-center justify-center ml-[8px] text-[#5f6368] cursor-help transition-colors duration-200 hover:text-[#1a73e8]" title="The extracted data from your JSON Path Query will appear here.">
                                                <Info size={14} />
                                            </div>
                                        </h3>
                                        <button className="px-[14px] py-[6px] text-[0.8125rem] font-medium rounded-[8px] border-[1px] border-[#dadce0] bg-white text-[#202124] flex items-center gap-[6px] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:border-[#bdc1c6] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3)]" onClick={() => handleCopy(JSON.stringify(pathResult, null, 2))}>
                                            <Copy size={14} /> Copy
                                        </button>
                                    </div>
                                    <div className="p-[20px] bg-[#fafafa] border-t-[1px] border-[#f1f3f4] max-h-[400px] overflow-y-auto flex-1">
                                        {pathResult ? (
                                            <pre style={{ margin: 0, fontSize: '0.8125rem', fontFamily: 'JetBrains Mono' }}>
                                                {JSON.stringify(pathResult, null, 2)}
                                            </pre>
                                        ) : (
                                            <span className="text-[#5f6368] text-[0.875rem] italic font-inherit">No query results...</span>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white border-[1px] border-[#dadce0] rounded-[12px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_1px_3px_0_rgba(60,64,67,0.3),0_4px_8px_3px_rgba(60,64,67,0.15)]">
                                    <div className="px-[16px] py-[10px] bg-[#f8f9fa] border-b-[1px] border-[#dadce0] flex justify-between items-center">
                                        <h3 className="text-[0.875rem] font-semibold text-[#202124] m-0 flex items-center gap-[8px]">
                                            <AlertCircle size={16} /> Schema Health
                                            <div className="inline-flex items-center justify-center ml-[8px] text-[#5f6368] cursor-help transition-colors duration-200 hover:text-[#1a73e8]" title="If a Schema is provided, this section shows whether your data matches the rules or lists specific validation errors.">
                                                <Info size={14} />
                                            </div>
                                        </h3>
                                    </div>
                                    <div className="p-[20px] bg-[#fafafa] border-t-[1px] border-[#f1f3f4] max-h-[400px] overflow-y-auto flex-1">
                                        {schemaErrors.length > 0 ? (
                                            <ul className="m-0 p-0 list-none flex flex-col gap-[12px]">
                                                {schemaErrors.map((err, i) => {
                                                    const line = findLineOfPath(err.instancePath);
                                                    return (
                                                        <li key={i} className="flex justify-between items-start p-[10px] bg-[#fef2f2] rounded-[8px] border-[1px] border-[#fee2e2]">
                                                            <div className="text-[#202124]">
                                                                <strong>{err.instancePath || 'root'}</strong>: {err.message}
                                                            </div>
                                                            {line && (
                                                                <button
                                                                    className="bg-white text-[#d93025] border-[1px] border-[#d93025] px-[8px] py-[2px] rounded-[4px] text-[0.75rem] font-semibold cursor-pointer whitespace-nowrap ml-[12px] transition-all duration-200 hover:bg-[#d93025] hover:text-white"
                                                                    onClick={() => scrollToLine(line)}
                                                                >
                                                                    Go to line {line}
                                                                </button>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : schemaInput ? (
                                            <div className="flex items-center gap-[8px] text-[#1e8e3e] text-[0.875rem] font-medium">
                                                <CheckCircle size={14} />
                                                Schema Validation Passed
                                            </div>
                                        ) : (
                                            <span className="text-[#5f6368] text-[0.875rem] italic font-inherit">No schema provided...</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ToolPageLayout >

            <ToolInfoStrip>
                <ToolInfoStrip.Section variant="privacy">
                    <h3><Shield size={20} />Privacy Shield</h3>
                    <p>100% Client-side. Your JSON never leaves your device.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Wand2 size={20} />Smart Repair</h3>
                    <p>Fixes missing quotes, trailing commas, and single quotes automatically.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Zap size={20} />Pro Tips</h3>
                    <p>Use JSON Path <code>$.*</code> to extract all root values instantly.</p>
                </ToolInfoStrip.Section>
            </ToolInfoStrip>

            <ToolSection variant="light" title="How to Use the JSON Utility" grid="steps">
                <ToolStepCard number={1} title="Enter Data" description="Paste your raw JSON into the editor. The tool will immediately lint and validate the syntax in real-time." />
                <ToolStepCard number={2} title="Apply Repairs" description="If errors are found, click &quot;Auto-Repair&quot; to fix common syntax issues automatically, and &quot;Beautify&quot; to format for readability." />
                <ToolStepCard number={3} title="Query & Export" description="Use the JSON Path field to extract specific data, or validate against a schema before copying the result to your clipboard." />
            </ToolSection>

            <ToolSection variant="muted" title="Why Use Our JSON Validator?" description="A professional-grade utility designed for developers who need speed, precision, and absolute privacy for their sensitive data." grid="features">
                <ToolFeatureCard><Wand2 size={28} /><h3>Smart Auto-Repair</h3><p>Tired of manually fixing missing commas or unquoted keys? Our smart engine repairs broken JSON structures instantly with one click.</p></ToolFeatureCard>
                <ToolFeatureCard><Search size={28} /><h3>JSON Path Querying</h3><p>Extract only the data you need from massive JSON files. Use standard query syntax to filter, slice, and pick specific data points.</p></ToolFeatureCard>
                <ToolFeatureCard><Shield size={28} /><h3>Local-First Privacy</h3><p>Unlike other online tools, we process everything on your machine. Your sensitive API keys and user data never leave your browser.</p></ToolFeatureCard>
                <ToolFeatureCard><CheckCircle size={28} /><h3>Schema Validation</h3><p>Ensure your JSON matches business rules. Paste a JSON Schema and get instant feedback on type mismatches and missing fields.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="muted" title="JSON Validator Use Cases" grid="features">
                <ToolFeatureCard><h3>API Debugging</h3><p>Quickly validate API responses and repair broken payloads before using them in your code or documentation.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Data Transformation</h3><p>Filter massive JSON datasets using JSON Path to isolate specific records without writing custom scripts.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Schema Development</h3><p>Test and refine your JSON Schemas against real data samples to ensure robust API contracts.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Configuration Management</h3><p>Validate complex configuration files before deployment to prevent runtime errors in production.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                <div className="flex flex-col gap-5 mb-8">
                    <ToolFeatureCard variant="faq"><h3>Is my JSON data secure?</h3><p>Yes, 100%. We use client-side processing, meaning your data stays entirely on your device. We do not store your JSON on our servers.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What is JSON Path?</h3><p>JSON Path is a query language for JSON, similar to XPath for XML. It allows you to specify a path to reach specific data within the structure.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Can it fix trailing commas?</h3><p>Yes. The "Auto-Repair" feature handles trailing commas, missing quotes, single quotes, and other common JSON syntax violations.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What is a JSON Schema?</h3><p>A JSON Schema is a JSON-based format for defining the structure, content, and validation rules for your JSON data objects.</p></ToolFeatureCard>
                </div>
                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
            </ToolSection>

            <ToolSection variant="muted" title="Related Developer Tools" grid="related">
                <ToolRelatedCard to="/tools/compare-text" icon={<Code size={24} />} title="Text Comparison" description="Compare JSON blobs or code snippets side-by-side with diff highlighting" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/scan-image-to-text" icon={<FileText size={24} />} title="OCR Extractor" description="Extract JSON content or text data from screenshots and images" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/qr-code-generator" icon={<Zap size={24} />} title="QR Generator" description="Convert your JSON strings or URLs into custom QR codes instantly" linkLabel="Try Tool" />
            </ToolSection>
        </>
    );
};

export default JSONValidator;
